import React from "react";
import { Typography } from "@kelp-dao/kernel-design-system";
import BaseImage from "@components/BaseImage";
import ResponsiveTooltip from "@components/Tooltip/ResponsiveTooltip";
import UTMLink from "@components/UTMLink";
import {
  STRATEGY_CONTRACT_HYPERLINK_CLICK,
  VAULT_DISCLAIMER_CLICKED,
} from "@constants/analytics";
import { trackEvent, getImageWithSubRoute } from "utils";

// Static content component - never re-renders after mount
const OverviewContent = React.memo(
  ({ TEXT_STRINGS, tokenType }: { TEXT_STRINGS: any; tokenType?: string }) => {
    const Stats = ({
      label,
      value,
      tooltipText,
      link,
      offerValue,
    }: {
      label: string;
      value: string;
      tooltipText?: string;
      link?: string;
      offerValue?: string;
    }) => {
      return (
        <div className="flex flex-row items-center">
          <div className="flex gap-1 items-center w-[120px] sm:w-[130px] tablet:w-[140px] shrink-0">
            <Typography
              type="body"
              variant="xsmall"
              className="text-xs font-normal leading-[14.4px] text-text-secondary"
            >
              {label}
            </Typography>
          </div>
          {link ? (
            <UTMLink
              href={link}
              isExternal
              className="flex gap-2 items-center"
              onClick={() => {
                if (label.toLowerCase().includes("contract")) {
                  trackEvent(STRATEGY_CONTRACT_HYPERLINK_CLICK, {
                    contract_address: value,
                    label: label,
                  });
                }
              }}
            >
              <Typography
                type="body"
                variant="xsmall"
                className={`text-xs font-normal leading-[140%] ${
                  offerValue
                    ? "line-through text-text-secondary font-light"
                    : "text-text-primary"
                }`}
              >
                {value}
              </Typography>
              {offerValue && (
                <Typography
                  type="body"
                  variant="xsmall"
                  className="text-xs font-normal leading-[140%] text-text-primary"
                >
                  {value}
                </Typography>
              )}
              {tooltipText && (
                <ResponsiveTooltip
                  placement="top"
                  content={
                    <Typography
                      type="body"
                      variant="xsmall"
                      className="font-normal text-xs text-text-primary"
                    >
                      {tooltipText}
                    </Typography>
                  }
                />
              )}
              <BaseImage
                src="/assets/external-link.svg"
                width={10}
                height={5}
                alt="external"
              />
            </UTMLink>
          ) : (
            <div className="flex gap-1.5">
              <Typography
                type="body"
                variant="xsmall"
                className={`text-xs font-normal leading-[140%] ${
                  offerValue
                    ? "line-through text-text-secondary font-light"
                    : "text-text-primary"
                }`}
              >
                {value}
              </Typography>
              {offerValue && (
                <Typography
                  type="body"
                  variant="xsmall"
                  className="text-xs font-semibold leading-[140%] text-text-primary"
                >
                  {offerValue}
                </Typography>
              )}
              {tooltipText && (
                <ResponsiveTooltip
                  placement="top"
                  content={
                    <Typography
                      type="body"
                      variant="xsmall"
                      className="font-normal text-xs text-text-primary"
                    >
                      {tooltipText}
                    </Typography>
                  }
                />
              )}
            </div>
          )}
        </div>
      );
    };

    const Partner = ({
      imgSrc,
      imageWidth,
    }: {
      imgSrc: string;
      imageWidth?: number;
    }) => (
      <BaseImage src={imgSrc} width={imageWidth || 180} height={"20"} alt="" />
    );

    return (
      <div className="flex flex-col md:flex-row gap-6 md:gap-8 justify-around sm:px-6 md:px-0">
        {/* Description - Left on desktop, Top on mobile */}
        <div className="w-full md:w-[40%] flex flex-col gap-3">
          <Typography
            type="body"
            variant="xsmall"
            className="text-xs font-semibold leading-[160%] text-text-primary"
          >
            Description
          </Typography>
          <Typography
            type="body"
            variant="xsmall"
            className="text-xs font-normal leading-[160%] text-text-secondary"
          >
            {TEXT_STRINGS.MAIN.desc}
          </Typography>
        </div>

        {/* Other Content - Right on desktop, Below on mobile */}
        <div className="w-full md:w-[50%] lg:w-[40%] flex flex-col gap-6">
          <div className="flex flex-col flex-wrap gap-2">
            {TEXT_STRINGS.FEES.data.map((item: any) => (
              <Stats
                key={item.key}
                label={item.title}
                value={item.value}
                offerValue={item.offerValue}
                {...(item.tooltip ? { tooltipText: item.tooltip } : {})}
                {...(item.link ? { link: item.link } : {})}
              />
            ))}

            {TEXT_STRINGS.PARTNERS?.details && (
              <div className="flex items-center">
                <Typography
                  type="body"
                  variant="xsmall"
                  className="text-xs font-normal leading-[14.4px] w-[120px] sm:w-[130px] tablet:w-[140px] shrink-0 text-text-secondary"
                >
                  Vault partners
                </Typography>
                <div className="flex gap-4 items-center">
                  {TEXT_STRINGS.PARTNERS.details.map((p: any) => (
                    <a
                      href={p.link}
                      key={p.key}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Partner
                        key={p.key}
                        imgSrc={p.imgSrc}
                        imageWidth={p.imageWidth}
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {TEXT_STRINGS.ASSETS?.list && (
              <div className="flex items-start">
                <Typography
                  type="body"
                  variant="xsmall"
                  className="text-xs font-normal leading-[14.4px] w-[120px] sm:w-[130px] tablet:w-[140px] shrink-0 text-text-secondary"
                >
                  Assets accepted
                </Typography>
                <div className="flex gap-1.5 flex-wrap justify-start">
                  {TEXT_STRINGS.ASSETS.list.map((item: any) => (
                    <div
                      key={item.key}
                      className="inline-flex items-center gap-1 px-1 py-0.5 rounded-full border border-border-default bg-bg-card text-text-secondary"
                    >
                      <img
                        src={getImageWithSubRoute(item.iconKey)}
                        alt={item.key}
                        className="h-3 w-3 tablet:h-4 tablet:w-4"
                      />
                      <Typography
                        type="body"
                        variant="xsmall"
                        className="text-[10px] font-normal leading-[14.4px]"
                      >
                        {item.title}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Typography
            type="body"
            variant="xsmall"
            className="text-[10px] font-normal leading-[160%] text-text-secondary"
          >
            Note: By accessing Gain, you acknowledge and agree to the risks
            involved and agree to the{" "}
            <a
              href={TEXT_STRINGS.MAIN.disclaimerLink}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-text-primary"
              onClick={() =>
                trackEvent(VAULT_DISCLAIMER_CLICKED, {
                  vault_name: tokenType,
                  location: "vault_overview",
                })
              }
            >
              Gain Disclaimers
            </a>
          </Typography>
        </div>
      </div>
    );
  }
);

OverviewContent.displayName = "OverviewContent";

export default OverviewContent;
